// TriageHome — the redesigned arrival "triage" Today (behind TRIAGE_V2, off by default).
// One question ("How are you arriving today?"), then a stripped-back home per route that
// reuses the app's existing tools, players, overlays and gating. Nothing here changes
// content or access; every opener is a handler App already owns.
//
// Rules of Hooks: TriageHome declares all its hooks at the top, before any conditional
// render. Every route/section below is its own component (own hooks, rendered as an
// element, never called as a function).
import React from 'react';
import { createPortal } from 'react-dom';
import type { Programme, Step } from '@/types';
import type { Meditation } from '@/data/meditations';
import { MEDITATIONS } from '@/data/meditations';
import { Screen } from '@/components/Screen';
import { BackButton } from '@/components/BackButton';
import { Icon, type IconName } from '@/components/icons';
import { useAuth } from '@/hooks/useAuth';
import { useProgress } from '@/hooks/useProgress';
import { canSync, openResearchCheckin, fetchMyProgress } from '@/services/sync';
import { fogsWaveFromMine } from '@/services/fogs';
import { Browser } from '@capacitor/browser';
import { researchCheckinDue, recordedWaveCount, lastWaveAt } from '@/services/researchCheckin';
import { dailyState } from '@/services/compassionDaily';
import { feedSteps, findStep, allSteps } from '@/services/contentProvider';
import { TODAY_ONE_JOB, TRIAL_FULL_LOCK } from '@/config/features';
import { Capacitor } from '@capacitor/core';
import { ReminderNudgeSheet } from '@/components/ReminderNudgeTile';
import { CalmYourBodyPanel } from '@/flows/calm/CalmYourBodyPanel';
import { ARRIVALS, VARIANTS, C, F, SIZE, W, SP, R, eyebrow } from '@/new/theme';
import { Tile, WaitingCard, dismissedToday, dismissForToday, LOCKED_LOOK, LockBadge, ARRIVAL_IMG, SH, SH_LIFT, HI, band } from '@/new/ArrivalHeader';
import {
  tileTarget, isRememberable, loadLastChoice, saveLastChoice, programmeProgressLine,
  topWaiting, meditationsBackTarget, programmeBackTarget,
  type TriageRoute, type TriageTileId, type TriageView,
} from './triageLogic';

export interface TriageHomeProps {
  /** Which triage screen is showing. CONTROLLED by App's Shell: choosing Recovery programme
   *  or Self-compassion changes the active programme, which re-keys (remounts) this
   *  component in the same render, so local state would be lost and the tap would bounce
   *  back to the question. App resets it to 'home' on every entry to Today and on user change. */
  view: TriageView;
  setView: (v: TriageView) => void;
  /** The active programme (main or self-compassion). */
  programme: Programme;
  /** Always the Recovery programme, whichever track is active (the hero progress line reads it). */
  mainProgramme: Programme;
  /** Renders the real Today/HomeScreen; `triage` makes it show the feed directly. */
  renderToday: (triage?: { underMarquee?: React.ReactNode; afterFeed?: React.ReactNode; header?: React.ReactNode }) => React.ReactNode;
  /** Bumped by App every time the Today tab is tapped (goTab('today')): back to the triage screen. */
  resetNonce: number;
  /** Bumped by App only when a session started by onSettleFirst runs to its natural end. */
  settleFirstCompletions: number;
  /** Ex route "Settle first": the free 2-minute session, tracked so only it can tick step 1. */
  onSettleFirst: () => void;
  onOpenStep: (step: Step) => void;
  onProgrammeOpened: () => void;
  /** Native (TODAY_ONE_JOB) post-lesson asks, same values HomeScreen receives. */
  deferredActSurvey: string | null;
  onOpenActSurvey: (actId: string) => void;
  pendingChapterFeedback: string[];
  onOpenChapterFeedback: () => void;
  onOpenCrisis: () => void;
  onOpenAsk: () => void;
  /** Opens the membership/unlock wall (same as other locked features). Used by the members-only Ask tile. */
  onUnlock?: () => void;
  onOpenToolkit: () => void;
  onOpenCare: () => void;
  onOpenJourney: () => void;
  onOpenTool: (toolId: string) => void;
  onOpenMeditation: (m: Meditation) => void;
  onOpenLibraryTheme: (theme: string) => void;
  onStartBreathing: (protocolId: string, careEligible?: boolean) => void;
  onSettleNowWithCare: () => void;
  calmLocked: boolean;
  featureLock: boolean;
  onCalmLocked: () => void;
  /** TRIAL_FULL_LOCK: the single locked action (App's unlock wall) for a lapsed non-member. */
  onLocked?: () => void;
  /** Last 3 days of the trial: the countdown card's tap (the paywall directly on iOS). */
  onTrialCountdown?: () => void;
  /** Ask ReNurture "Calm" button: raised by App, consumed here (opens the emotions chooser). */
  openCalmOnMount: boolean;
  onCalmOpened: () => void;
  /** Right after onboarding: land in the current lesson (via the programme feed). */
  openProgrammeOnMount: boolean;
}

// Views a lapsed non-member (TRIAL_FULL_LOCK) cannot reach: everything but Today, Self-compassion
// and (via the pill) crisis support.
const LOCKED_VIEWS: ReadonlySet<TriageView> = new Set<TriageView>([
  'triggered', 'ex', 'programme', 'programmeFromCompassion', 'meditations', 'meditationsFromTriggered',
]);

/** Locked tile ids on the Today selector (all but the crisis pill and the Self-compassion row). */
export const isTriageTileLocked = (id: TriageTileId, trialLock: boolean) =>
  trialLock && id !== 'unsafe' && id !== 'compassion';

export function TriageHome(props: TriageHomeProps) {
  const progress = useProgress();
  const auth = useAuth();
  const trialLock = TRIAL_FULL_LOCK && !!auth.featuresLocked;
  // A route that was open when the trial lapsed (or a restored view) falls back to the selector.
  const view: TriageView = trialLock && LOCKED_VIEWS.has(props.view) ? 'home' : props.view;
  const setView = (v: TriageView) => {
    if (trialLock && LOCKED_VIEWS.has(v)) { props.onLocked?.(); return; }
    props.setView(v);
  };
  const [calmOpen, setCalmOpen] = React.useState(!!props.openCalmOnMount);

  // Tapping the Today tab (or any "back to Today" exit) closes the emotions overlay; App
  // itself puts the view back to 'home'.
  const firstReset = React.useRef(true);
  React.useEffect(() => {
    if (firstReset.current) { firstReset.current = false; return; }
    setCalmOpen(false);
  }, [props.resetNonce]);

  // Ask ReNurture's "Calm" button, raised while Today is already mounted.
  React.useEffect(() => {
    if (!props.openCalmOnMount) return;
    if (trialLock) { props.onCalmOpened(); props.onLocked?.(); return; }
    setView('triggered');
    setCalmOpen(true);
    props.onCalmOpened();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.openCalmOnMount]);

  React.useEffect(() => {
    // Right after onboarding: open the current lesson but stay on the triage screen behind it
    // (closing it lands on the triage question, where the first check-in is waiting), as
    // HomeScreen does for the non-triage build.
    if (!props.openProgrammeOnMount) return;
    if (trialLock) { props.onProgrammeOpened(); return; }
    if (progress.state.activeProgramme !== 'compassion') {
      const id = feedSteps(props.programme, progress.state)[0]?.id;
      const st = (id ? findStep(props.programme, id) : undefined) ?? allSteps(props.programme)[0];
      if (st) props.onOpenStep(st);
    }
    props.onProgrammeOpened();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.openProgrammeOnMount]);

  // The view owns the track: a restored view (e.g. Back from the course map after toggling the
  // track there) must not disagree with activeProgramme.
  const wantTrack =
    view === 'compassion' ? 'compassion'
    : view === 'programme' || view === 'programmeFromCompassion' ? 'main'
    : null;
  const activeTrack = progress.state.activeProgramme;
  React.useEffect(() => {
    if (wantTrack && wantTrack !== activeTrack) progress.setActiveProgramme(wantTrack);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantTrack, activeTrack]);

  const go = (v: TriageView) => setView(v);
  const home =() => { setCalmOpen(false); setView('home'); };

  const calmOverlay = calmOpen
    ? createPortal(
        <div
          role="dialog"
          aria-label="Calm your body"
          style={{
            position: 'fixed', inset: 0, zIndex: 75, overflowY: 'auto',
            background: 'var(--bg, #F7F5F2)',
            padding: 'calc(var(--safe-top) + 14px) 16px calc(var(--safe-bottom) + 28px)',
            animation: 'rise .32s cubic-bezier(.2,.7,.3,1)',
          }}
        >
          <div style={{ maxWidth: 560, margin: '0 auto' }}>
            <CalmYourBodyPanel
              key="settle"
              initialStep="emotions"
              newUi
              standardBack
              onStartBreathing={props.onStartBreathing}
              onOpenCrisis={props.onOpenCrisis}
              onOpenTool={(id) => { setCalmOpen(false); props.onOpenTool(id); }}
              onOpenLibraryTheme={(t) => { setCalmOpen(false); props.onOpenLibraryTheme(t); }}
              onOpenCare={props.onOpenCare}
              collapsed={false}
              onCollapse={() => setCalmOpen(false)}
              onExpand={() => {}}
              locked={props.calmLocked}
              featureLock={props.featureLock}
              onLocked={props.onCalmLocked}
            />
          </div>
        </div>,
        document.body,
      )
    : null;

  let body: React.ReactNode;
  switch (view) {
    case 'triggered':
      body = <TriggeredHome props={props} onBack={home} onTellMe={() => setCalmOpen(true)} onMeditations={() => go('meditationsFromTriggered')} />;
      break;
    case 'ex':
      body = <ExHome props={props} onBack={home} />;
      break;
    case 'programme':
    case 'programmeFromCompassion':
      body = <ProgrammeHome props={props} onBack={() => {
        const target = programmeBackTarget(view);
        // Back to the Self-compassion page must put its track back, or the page shows the Recovery feed.
        if (target === 'compassion') progress.setActiveProgramme('compassion');
        go(target);
      }} />;
      break;
    case 'compassion':
      body = <CompassionHome props={props} trialLock={trialLock} onBack={home} onProgramme={() => go('programmeFromCompassion')} />;
      break;
    case 'meditations':
      body = <MeditationsView props={props} onBack={home} />;
      break;
    case 'meditationsFromTriggered':
      body = <MeditationsView props={props} onBack={() => go(meditationsBackTarget(view))} />;
      break;
    default:
      body = <TriageSelector props={props} onRoute={(r) => go(r)} />;
  }

  return (
    <>
      {body}
      {calmOverlay}
    </>
  );
}

// ── shared presentation ────────────────────────────────────────────────────────────

const quietLink: React.CSSProperties = {
  border: 'none', background: 'none', cursor: 'pointer', padding: '6px 2px',
  fontFamily: F.ui, fontSize: SIZE.md, fontWeight: W.semibold, color: C.brandDark,
};

// Area title + one-line blurb shown at the top of each route page (replaces the greeting).
export const ROUTE_COPY = {
  triggered: { title: 'Emotionally triggered', blurb: 'Calm your body when something sets you off, then retrain your responses so triggers lose their hold.' },
  ex: { title: 'Dealing with the ex', blurb: 'Step out of your ex’s attempts to control, gaslight and undermine you.' },
  programme: { title: 'Recovery programme', blurb: 'The full step-by-step path to understand what happened, heal from it and rebuild your life.' },
  compassion: { title: 'Self-compassion course', blurb: 'A gentle daily practice that works alongside your Recovery programme.' },
  meditations: { title: 'Meditations', blurb: 'Guided practices to settle your body, steady your mind and come back to yourself.' },
} as const;
type RouteCopyKey = keyof typeof ROUTE_COPY;

function AreaHeading({ area }: { area: RouteCopyKey }) {
  const c = ROUTE_COPY[area];
  return (
    <>
      <h1 style={{ fontFamily: F.title, fontSize: 29, fontWeight: W.medium, letterSpacing: '-0.4px', lineHeight: 1.15, color: C.text, margin: '2px 0 0' }}>{c.title}</h1>
      <p style={{ fontFamily: F.ui, fontSize: SIZE.md, lineHeight: 1.5, color: C.textMuted, margin: '8px 0 0' }}>{c.blurb}</p>
    </>
  );
}

function RouteFrame({ area, children, onBack }: { area: RouteCopyKey; children: React.ReactNode; onBack: () => void }) {
  return (
    <Screen label="Today" surface="deep">
      <div style={{ ...band, paddingTop: 24, paddingBottom: 28, animation: 'arriveIn 460ms cubic-bezier(0.22,0.61,0.36,1) both' }}>
        <div style={{ marginBottom: 6 }}><BackButton onClick={onBack} /></div>
        <AreaHeading area={area} />
        {children}
      </div>
    </Screen>
  );
}

// The "matched box" from ArrivalHeader phase 2: tinted in the arrival's tone, hand-painted
// overlay faded on the left, eyebrow + headline + meta + buttons.
// Guide me: soft teal in the same family as the arrival tones (was a dark teal gradient).
const GUIDE_TONE = '#d3e8e5';
const GUIDE_INK = '#22524f';

function MatchedBox({ tone, ink, image, headline, em, meta, children }: {
  tone: string; ink: string; image?: string; headline: string; em: string; meta: string; children: React.ReactNode;
}) {
  return (
    <section style={{ position: 'relative', overflow: 'hidden', background: `linear-gradient(158deg, rgba(255,255,255,0.42), rgba(255,255,255,0) 60%), ${tone}`, borderRadius: 22, padding: '20px 20px 18px', marginTop: SP.lg, boxShadow: `${SH_LIFT}, ${HI}` }}>
      {image && (
        <img src={image} alt="" aria-hidden decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.4, pointerEvents: 'none', WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 42%, #000 80%)', maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 42%, #000 80%)' }} />
      )}
      <div style={{ position: 'relative' }}>
        <h2 style={{ fontFamily: F.title, fontSize: SIZE.h2, fontWeight: W.medium, lineHeight: 1.15, letterSpacing: '-0.3px', color: C.text, margin: 0 }}>
          {headline} <em style={{ fontStyle: 'italic', color: ink }}>{em}</em>
        </h2>
        <p style={{ fontFamily: F.ui, fontSize: SIZE.body, lineHeight: 1.5, color: C.textBody, margin: '8px 0 14px' }}>{meta}</p>
        {children}
      </div>
    </section>
  );
}

function boxButtons(ink: string) {
  const primary: React.CSSProperties = { background: 'linear-gradient(180deg,#ffffff,#f3f6f6)', color: ink, border: 'none', borderRadius: R.md, cursor: 'pointer', fontFamily: F.ui, fontWeight: W.semibold, boxShadow: '0 3px 10px rgba(30,50,60,0.15), inset 0 1px 0 rgba(255,255,255,0.7)' };
  const soft: React.CSSProperties = { background: 'rgba(255,255,255,0.5)', color: ink, border: 'none', borderRadius: R.md, cursor: 'pointer', fontFamily: F.ui, fontWeight: W.semibold, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6), 0 1px 3px rgba(30,50,60,0.1)' };
  return { primary, soft };
}

function ListRow({ icon, title, sub, tag, done, marker, onClick }: { icon: IconName; title: string; sub?: string; tag?: string; done?: boolean; marker?: number; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: SP.md, width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer', background: 'linear-gradient(180deg,#ffffff,#fdfbf7)', borderRadius: 16, padding: '12px 14px', boxShadow: `${SH}, ${HI}` }}
    >
      <span aria-hidden style={{ width: 34, height: 34, borderRadius: 11, background: 'rgba(46,138,134,0.12)', color: C.brandDark, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {marker ? <span style={{ fontFamily: F.ui, fontSize: SIZE.md, fontWeight: W.semibold }}>{marker}</span> : <Icon name={icon} size={17} />}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontFamily: F.title, fontSize: SIZE.h3, fontWeight: W.medium, color: C.text }}>{title}</span>
        {sub && <span style={{ display: 'block', fontFamily: F.ui, fontSize: SIZE.caption, color: C.textMuted, marginTop: 1 }}>{sub}</span>}
      </span>
      {tag && <span style={{ fontFamily: F.ui, fontSize: 10.5, fontWeight: W.bold, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.textMuted }}>{tag}</span>}
      {done ? <span aria-label="Done" style={{ color: C.brandDark, display: 'flex' }}><Icon name="check" size={16} /></span> : <span aria-hidden style={{ color: C.textFaint, display: 'flex' }}><Icon name="chevRight" size={16} /></span>}
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ ...eyebrow, color: C.textMuted, margin: '22px 0 11px' }}>{children}</div>;
}

// ── the triage screen ──────────────────────────────────────────────────────────────

const TILE_COPY: Record<Exclude<TriageTileId, 'unsafe' | 'notsure'>, { title: string; sub: string }> = {
  triggered: { title: ARRIVALS.find((a) => a.id === 'triggered')!.label, sub: ARRIVALS.find((a) => a.id === 'triggered')!.sub },
  ex: { title: ARRIVALS.find((a) => a.id === 'ex')!.label, sub: ARRIVALS.find((a) => a.id === 'ex')!.sub },
  programme: { title: 'Recovery programme', sub: 'Pick up your next lesson' },
  compassion: { title: 'Self-compassion course', sub: 'Today’s practice · free' },
};

const lastChip = (
  <span style={{ position: 'absolute', top: 10, right: 12, fontFamily: F.ui, fontSize: 9.5, fontWeight: W.bold, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(60,50,30,0.62)', background: 'rgba(255,255,255,0.55)', borderRadius: 999, padding: '2px 8px' }}>Last time</span>
);

const welcomeKey = (userId: string | null) => `renurture.triage.welcomed.${userId ?? 'anon'}`;

/** True the first time this device shows the selector for this user. Any storage failure => false ("Welcome back"). */
export function isFirstWelcome(userId: string | null): boolean {
  try { return localStorage.getItem(welcomeKey(userId)) === null; } catch { return false; }
}
function markWelcomed(userId: string | null) {
  try { localStorage.setItem(welcomeKey(userId), '1'); } catch { /* storage unavailable: keep "Welcome back" */ }
}

function TriageSelector({ props, onRoute }: { props: TriageHomeProps; onRoute: (r: Exclude<TriageRoute, 'home'>) => void }) {
  const progress = useProgress();
  const waiting = useWaiting(props);
  const stripRef = React.useRef<HTMLDivElement | null>(null);
  const [last, setLast] = React.useState<TriageTileId | null>(() => loadLastChoice(typeof sessionStorage !== 'undefined' ? sessionStorage : null));
  const auth = useAuth();
  const session = auth.session;
  const trialLock = TRIAL_FULL_LOCK && !!auth.featuresLocked;
  const [, bumpBanner] = React.useReducer((n: number) => n + 1, 0);
  const daysLeft = auth.trialDaysLeft;
  // Only where a purchase route exists (App passes onTrialCountdown with the iOS paywall or the web buy page).
  const showCountdown = !!props.onTrialCountdown && TRIAL_FULL_LOCK && !!auth.trialActive && daysLeft != null && daysLeft >= 1 && daysLeft <= 3 && !dismissedToday('trialcountdown');
  // Same notion of membership the app uses everywhere (useAuth.isMember). Unknown (auth off / no session) behaves as member.
  const askLocked = !!auth.enabled && !!session && auth.isMember === false && !!props.onUnlock;
  const firstName = session?.user.firstName?.trim() || null;
  const welcomeUserId = session?.user.userId ?? null;
  // Decided once per mount so the line never flips mid-session; "seen" is recorded after mount.
  const [firstVisit] = React.useState(() => isFirstWelcome(welcomeUserId));
  React.useEffect(() => { if (firstVisit) markWelcomed(welcomeUserId); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const welcome = `${firstVisit ? 'Welcome' : 'Welcome back'}${firstName ? `, ${firstName}` : ''}`;

  const choose = (id: TriageTileId) => {
    if (isTriageTileLocked(id, trialLock)) return props.onLocked?.();
    if (id === 'notsure' && askLocked) return props.onUnlock?.();
    if (isRememberable(id)) {
      saveLastChoice(typeof sessionStorage !== 'undefined' ? sessionStorage : null, id);
      setLast(id);
    }
    const t = tileTarget(id);
    if (t.kind === 'crisis') return props.onOpenCrisis();
    if (t.kind === 'ask') return props.onOpenAsk();
    // Same call the route toggle makes; the programme/compassion homes then read it.
    if (t.route === 'programme') progress.setActiveProgramme('main');
    if (t.route === 'compassion') progress.setActiveProgramme('compassion');
    onRoute(t.route);
  };

  const unsafe = ARRIVALS.find((a) => a.id === 'unsafe')!;
  const triggered = ARRIVALS.find((a) => a.id === 'triggered')!;
  const ex = ARRIVALS.find((a) => a.id === 'ex')!;
  const scArrival = ARRIVALS.find((a) => a.id === 'hardweek')!; // sage compassion tone
  const chipFor = (id: TriageTileId) => (last === id ? lastChip : undefined);
  const programmeLine = programmeProgressLine(props.mainProgramme, progress.state);

  return (
    <Screen label="Today" surface="deep">
      <div style={{ ...band, paddingTop: 20, paddingBottom: 34 }}>
        <div style={{ fontFamily: F.ui, fontSize: SIZE.md, lineHeight: '18px', color: C.textMuted, marginBottom: 4 }}>
          {welcome}
          {waiting.shown.length > 0 && (
            <>
              {' · '}
              <button
                type="button"
                onClick={() => stripRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                style={{ display: 'inline', padding: '6px 2px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: F.ui, fontSize: SIZE.md, lineHeight: '18px', fontWeight: W.semibold, color: C.brandDark }}
              >
                {waiting.shown.length === 1 ? '1 thing waiting' : `${waiting.shown.length} things waiting`}
                <span aria-hidden>{' ›'}</span>
              </button>
            </>
          )}
        </div>
        <h1 style={{ fontFamily: F.title, fontSize: SIZE.h1, fontWeight: W.regular, lineHeight: 1.12, letterSpacing: '-0.5px', color: C.text, margin: 0 }}>
          How are you <em style={{ fontStyle: 'italic', fontWeight: W.medium }}>arriving today?</em>
        </h1>
        <button
          type="button"
          aria-label="Feeling unsafe, crisis support"
          onClick={() => choose('unsafe')}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 48, marginTop: 18,
            padding: '0 16px', border: 'none', cursor: 'pointer', textAlign: 'left',
            background: unsafe.tone, color: unsafe.ink, borderRadius: R.pill, boxShadow: `${SH}, ${HI}`,
          }}
        >
          <span aria-hidden style={{ display: 'flex', flexShrink: 0 }}><Icon name="shield" size={18} /></span>
          <span style={{ flex: 1, minWidth: 0, fontFamily: F.ui, fontSize: SIZE.md }}>
            <span style={{ fontWeight: W.semibold }}>Feeling unsafe</span>
            <span style={{ opacity: 0.78 }}>{' · Crisis support'}</span>
          </span>
          <span aria-hidden style={{ display: 'flex', flexShrink: 0 }}><Icon name="chevRight" size={16} /></span>
        </button>

        {trialLock && <TrialBanner title="Your free trial has ended" sub="Become a member to unlock everything. Feeling unsafe and the Self-compassion course stay free." button="See membership" onTap={() => props.onLocked?.()} />}
        {showCountdown && (
          <TrialBanner
            title={`${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left of your free trial`}
            sub="See what membership includes"
            onTap={() => props.onTrialCountdown?.()}
            onDismiss={() => { dismissForToday('trialcountdown'); bumpBanner(); }}
          />
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          <Tile tone={triggered.tone} ink={triggered.ink} title={TILE_COPY.triggered.title} sub={TILE_COPY.triggered.sub} motif={triggered.motif} image={ARRIVAL_IMG.triggered} big badge={chipFor('triggered')} locked={trialLock} onClick={() => choose('triggered')} />
          <Tile tone={ex.tone} ink={ex.ink} title={TILE_COPY.ex.title} sub={TILE_COPY.ex.sub} motif={ex.motif} image={ARRIVAL_IMG.ex} big badge={chipFor('ex')} locked={trialLock} onClick={() => choose('ex')} />
          <Tile tone="#f1d9b4" ink="#5a4218" title={TILE_COPY.programme.title} sub={`Your main path · ${programmeLine?.replace(" · ", ", ") ?? TILE_COPY.programme.sub}`} motif="horizon" image="/newbuttonimages/today_continueprogramme.png" imageOpacity={0.4} full big primary badge={chipFor('programme')} locked={trialLock} onClick={() => choose('programme')} />
        </div>

        <button
          type="button"
          onClick={() => choose('compassion')}
          style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: SP.md, width: '100%', marginTop: 12, textAlign: 'left', border: 'none', cursor: 'pointer', background: scArrival.tone, color: scArrival.ink, borderRadius: 16, padding: '12px 14px', boxShadow: `${SH}, ${HI}` }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontFamily: F.title, fontSize: SIZE.h3, fontWeight: W.medium, color: scArrival.ink }}>{TILE_COPY.compassion.title}</span>
            <span style={{ display: 'block', fontFamily: F.ui, fontSize: SIZE.caption, color: scArrival.ink, marginTop: 1 }}>{TILE_COPY.compassion.sub}</span>
          </span>
          {last === 'compassion' && <span style={{ fontFamily: F.ui, fontSize: 9.5, fontWeight: W.bold, letterSpacing: '0.08em', textTransform: 'uppercase', color: scArrival.ink, background: 'rgba(60,50,30,0.08)', borderRadius: 999, padding: '2px 8px' }}>Last time</span>}
        </button>

        <button
          onClick={() => choose('notsure')}
          aria-label={trialLock ? 'Guide me, locked, for members' : undefined}
          style={{
            position: 'relative', overflow: 'hidden', width: '100%', marginTop: 14,
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
            textAlign: 'left', border: 'none', cursor: 'pointer',
            background: `linear-gradient(158deg, rgba(255,255,255,0.42), rgba(255,255,255,0) 60%), ${GUIDE_TONE}`, color: GUIDE_INK, borderRadius: 20, padding: '16px 18px',
            ...(askLocked ? { filter: 'saturate(0.6)' } : null),
            ...(trialLock ? LOCKED_LOOK : null),
            boxShadow: `${SH}, ${HI}`,
          }}
        >
          {trialLock && <LockBadge />}
          <img src="/newbuttonimages/today_calm-your-body-first-overlay.webp" alt="" aria-hidden decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.4, pointerEvents: 'none', WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 40%, #000 78%)', maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 40%, #000 78%)' }} />
          <span style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: F.title, fontSize: SIZE.h3, fontWeight: W.medium, lineHeight: 1.15 }}>Guide me</span>
            {last === 'notsure' && <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', background: 'rgba(34,82,79,0.1)', borderRadius: 999, padding: '2px 7px' }}>Last time</span>}
          </span>
          <span style={{ position: 'relative', fontFamily: F.ui, fontSize: SIZE.caption }}>{askLocked ? 'Personal AI guidance · for members' : <>Tell me what{'’'}s going on</>}</span>
        </button>

        <div ref={stripRef}><WaitingStrip waiting={waiting} /></div>
      </div>
    </Screen>
  );
}

// TRIAL_FULL_LOCK: the trial-ended card (not dismissible) and the last-3-days countdown (dismissible for the day).
function TrialBanner({ title, sub, button, onTap, onDismiss }: { title: string; sub: string; button?: string; onTap: () => void; onDismiss?: () => void }) {
  return (
    <div role="region" aria-label={title} style={{ display: 'flex', alignItems: 'center', gap: SP.md, marginTop: 12, padding: '14px 14px 14px 16px', borderRadius: 18, background: 'linear-gradient(180deg,#fff8e8,#fbefd2)', boxShadow: `${SH}, ${HI}, inset 0 0 0 1px rgba(201,155,29,0.32)` }}>
      <button onClick={onTap} style={{ flex: 1, minWidth: 0, textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
        <span style={{ display: 'block', fontFamily: F.title, fontSize: SIZE.h3, fontWeight: W.medium, color: C.text }}>{title}</span>
        <span style={{ display: 'block', fontFamily: F.ui, fontSize: SIZE.caption, color: C.textMuted, marginTop: 2 }}>{sub}</span>
        {button && <span style={{ display: 'inline-block', marginTop: 10, padding: '9px 16px', borderRadius: R.md, background: 'linear-gradient(150deg,#f0cd6f,#d9a52f)', color: '#fff', fontFamily: F.ui, fontSize: SIZE.md, fontWeight: W.semibold }}>{button}</span>}
      </button>
      {onDismiss && (
        <button aria-label="Dismiss" onClick={onDismiss} style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.textFaint, fontSize: 18, lineHeight: 1, padding: '4px 6px', flexShrink: 0, alignSelf: 'flex-start' }}>×</button>
      )}
    </div>
  );
}

// "Waiting on you": the same two cards, same conditions and openers the app uses today
// (check-in due -> web check-in; CARE pending -> CARE overlay with a dismiss ×).
type WaitingItem = { key: string; icon: IconName; title: string; sub: string; onTap: () => void; onDismiss?: () => void; locked?: boolean };
interface Waiting { shown: WaitingItem[]; remindersSheetOpen: boolean; closeRemindersSheet: () => void }

// Single source for the "Waiting on you" list: the strip and the header count line both read it.
function useWaiting(props: TriageHomeProps): Waiting {
  const [, bump] = React.useReducer((n: number) => n + 1, 0);
  const [remindersSheetOpen, setRemindersSheetOpen] = React.useState(false);
  const auth = useAuth();
  const progress = useProgress();
  const trialLock = TRIAL_FULL_LOCK && !!auth.featuresLocked;
  const canCheckin = canSync(auth.session) && progress.state.userType === 'survivor';
  const token = canSync(auth.session) ? auth.session.accessToken : null;

  React.useEffect(() => {
    if (!token) return;
    let alive = true;
    const refresh = () => {
      fetchMyProgress(token)
        .then((ins) => {
          if (!alive || !ins) return;
          const count = recordedWaveCount(ins);
          if (count == null) return;
          progress.setCompletedCheckinCount(count, lastWaveAt(ins));
          // The full FOGS check, saved against the research ID for every signed-in user (research use only with consent): a recorded
          // fogs-v1 wave means it is done, and its flagged sections feed the Progress card.
          const fogs = fogsWaveFromMine(ins);
          if (fogs) progress.setFogsResult(fogs.flagged, fogs.at ?? undefined);
        })
        .catch(() => {});
    };
    refresh();
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', refresh);
    // Native: the in-app browser (check-in or full check) was closed with Done/X. Retry once
    // shortly after, in case Done beat the page's keepalive save to the server.
    let retry: ReturnType<typeof setTimeout> | undefined;
    const finished = Capacitor.isNativePlatform()
      ? Browser.addListener('browserFinished', () => { refresh(); clearTimeout(retry); retry = setTimeout(refresh, 2500); })
      : null;
    return () => {
      alive = false;
      clearTimeout(retry);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', refresh);
      finished?.then((h) => h.remove()).catch(() => {});
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const completedCount = progress.state.completedCheckinCount;
  const due = canCheckin
    ? researchCheckinDue(progress.state, Date.now(), completedCount).due
    : import.meta.env.DEV;
  const startCheckin = () => {
    const s = auth.session;
    if (!canSync(s) || !s.accessToken) return;
    const firstCheckin = completedCount === 0;
    const consent = progress.state.researchConsent === 'granted';
    void openResearchCheckin(s.accessToken, undefined, { firstCheckin, consent });
    progress.markResearchCheckinOpened();
  };

  // Same cards, copy and openers as ArrivalHeader's "Waiting on you" (+ HomeScreen's native
  // extras); the top two by priority are shown.
  const items: WaitingItem[] = [];
  if (due) items.push({ key: 'checkin', icon: 'heart', title: 'Complete a check-in', sub: 'A short, private wellbeing check-in', onTap: startCheckin });
  if (progress.state.careTriggered)
    items.push({ key: 'care', icon: 'sparkle', title: 'Complete the CARE steps', sub: 'Critical task to retrain your triggered trauma responses', locked: trialLock, onTap: trialLock ? () => props.onLocked?.() : props.onOpenCare, onDismiss: () => progress.dismissCare() });
  if (TODAY_ONE_JOB) {
    if (props.deferredActSurvey && !dismissedToday('actsurvey')) {
      const id = props.deferredActSurvey;
      items.push({ key: 'actsurvey', icon: 'chart', title: 'A short wellbeing check', sub: 'Six quick questions, whenever suits', onTap: () => props.onOpenActSurvey(id) });
    }
    if (props.pendingChapterFeedback.length > 0 && !dismissedToday('chapterfeedback'))
      items.push({ key: 'chapterfeedback', icon: 'pencil', title: 'How was that chapter?', sub: 'A quick thought, only if you like', onTap: props.onOpenChapterFeedback });
  }
  const remindersSet = !!progress.state.morningAnchorTime;
  if (!remindersSet && !dismissedToday('reminders') && Capacitor.isNativePlatform())
    items.push({ key: 'reminders', icon: 'clock', title: 'Set up a daily reminder', sub: 'A gentle nudge to return each day', onTap: () => { setRemindersSheetOpen(true); dismissForToday('reminders'); bump(); }, onDismiss: () => { dismissForToday('reminders'); bump(); } });
  if ((progress.state.calmSessionsCount ?? 0) === 0 && !dismissedToday('trybreathing'))
    items.push({ key: 'trybreathing', icon: 'moon', title: 'Try a short breathing exercise', sub: 'Choose how long, then breathe with us', locked: trialLock, onTap: () => { if (trialLock) { props.onLocked?.(); return; } props.onStartBreathing('relax_box'); dismissForToday('trybreathing'); bump(); }, onDismiss: () => { dismissForToday('trybreathing'); bump(); } });

  return { shown: topWaiting(items), remindersSheetOpen, closeRemindersSheet: () => setRemindersSheetOpen(false) };
}

function WaitingStrip({ waiting }: { waiting: Waiting }) {
  const { shown } = waiting;
  return (
    <>
      {waiting.remindersSheetOpen && <ReminderNudgeSheet onClose={waiting.closeRemindersSheet} />}
      {shown.length > 0 && (
        <>
          <SectionLabel>Waiting on you</SectionLabel>
          <div style={{ display: 'grid', gap: SP.sm }}>
            {shown.map((it) => <WaitingCard key={it.key} icon={it.icon} title={it.title} sub={it.sub} onTap={it.onTap} onDismiss={it.onDismiss} locked={it.locked} />)}
          </div>
        </>
      )}
    </>
  );
}

// ── route homes ────────────────────────────────────────────────────────────────────

function TriggeredHome({ props, onBack, onTellMe, onMeditations }: { props: TriageHomeProps; onBack: () => void; onTellMe: () => void; onMeditations: () => void }) {
  const v = VARIANTS.triggered;
  const { primary, soft } = boxButtons(v.ink);
  const grounding = MEDITATIONS.find((m) => m.title === 'Grounding Exercise');
  return (
    <RouteFrame area="triggered" onBack={onBack}>
      <MatchedBox tone={v.tone} ink={v.ink} image={ARRIVAL_IMG.triggered} headline="Calm your" em="body first." meta="Start with a short settling breath, or tell us how it feels and we’ll match the exercise.">
        <div style={{ display: 'grid', gap: SP.sm }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: SP.sm, fontFamily: F.ui, fontSize: SIZE.caption, fontWeight: W.semibold, color: v.ink }}>
            <span aria-hidden style={{ width: 21, height: 21, borderRadius: R.pill, border: `1px solid ${v.ink}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0 }}>1</span>
            <span>Step 1</span>
          </div>
          <button onClick={props.onSettleNowWithCare} style={{ ...primary, padding: '12px 16px', fontSize: SIZE.md, textAlign: 'left' }}>{'Breathe with me · 2 min'}</button>
          <button onClick={onTellMe} style={{ ...soft, padding: '12px 16px', fontSize: SIZE.md, textAlign: 'left' }}>{'Tell me how it feels →'}</button>
        </div>
        <div style={{ display: 'grid', gap: SP.sm, marginTop: SP.md }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: SP.sm, fontFamily: F.ui, fontSize: SIZE.caption, fontWeight: W.semibold, color: v.ink }}>
            <span aria-hidden style={{ width: 21, height: 21, borderRadius: R.pill, border: `1px solid ${v.ink}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0 }}>2</span>
            <span>Step 2</span>
          </div>
          <button onClick={props.onOpenCare} style={{ ...soft, padding: '12px 16px', fontSize: SIZE.md, textAlign: 'left', width: '100%' }}>
            <span style={{ display: 'block' }}>Learn to be less triggered in the future</span>
            <span style={{ display: 'block', fontSize: SIZE.caption, fontWeight: W.regular, color: C.textMuted, marginTop: 2 }}>The CARE steps</span>
          </button>
        </div>
      </MatchedBox>
      <SectionLabel>More to help you settle</SectionLabel>
      <div style={{ display: 'grid', gap: SP.sm }}>
        {grounding && <ListRow icon="moon" title="Grounding Exercise" onClick={() => props.onOpenMeditation(grounding)} />}
        <ListRow icon="moon" title="All meditations" onClick={onMeditations} />
      </div>
      <SectionLabel>Read when you{'’'}re ready</SectionLabel>
      <div style={{ display: 'grid', gap: SP.sm }}>
        <ListRow icon="reflections" title="Calming trauma" onClick={() => props.onOpenLibraryTheme('Calm')} />
      </div>
    </RouteFrame>
  );
}

const EX_TOOLS: Array<{ id: string; title: string }> = [
  { id: 'beaturge', title: 'Resist the urge to contact your ex' },
  { id: 'boundary', title: 'Deal with your ex’s ongoing interference' },
  { id: 'communicate', title: 'Communicating your boundaries' },
  { id: 'escalation', title: 'What to do if their behaviour escalates' },
];

function ExHome({ props, onBack }: { props: TriageHomeProps; onBack: () => void }) {
  const v = VARIANTS.ex;
  const { primary } = boxButtons(v.ink);
  // "Done" only once a "Settle first" session started since this screen opened ran to its
  // natural end. App counts only those sessions (pending marker cleared on any other end).
  const [baseline] = React.useState(props.settleFirstCompletions);
  const settled = props.settleFirstCompletions > baseline;
  return (
    <RouteFrame area="ex" onBack={onBack}>
      <MatchedBox tone={v.tone} ink={v.ink} image={ARRIVAL_IMG.ex} headline="Calm first," em="then deal with them." meta={'If you\u2019re angry, anxious or shaken, you\u2019ll react to their behaviour instead of responding to it. Settle your body first, then deal with them with a clear head.'}>
        <button
          onClick={props.onSettleFirst}
          style={{ ...primary, padding: '12px 16px', fontSize: SIZE.md, textAlign: 'left', display: 'flex', alignItems: 'center', gap: SP.sm, width: '100%' }}
        >
          {settled && <span aria-hidden style={{ display: 'flex', flexShrink: 0 }}><Icon name="check" size={14} /></span>}
          <span>{'1 · Settle myself first · 2 min'}{settled ? ' · done' : ''}</span>
        </button>
      </MatchedBox>
      <SectionLabel>2 · Then: what{'’'}s happening?</SectionLabel>
      <div style={{ display: 'grid', gap: SP.sm }}>
        {EX_TOOLS.map((t) => <ListRow key={t.id} icon="shield" title={t.title} onClick={() => props.onOpenTool(t.id)} />)}
      </div>
      <SectionLabel>Read when you{'’'}re ready</SectionLabel>
      <div style={{ display: 'grid', gap: SP.sm }}>
        <ListRow icon="reflections" title="Boundaries" onClick={() => props.onOpenLibraryTheme('Hold')} />
        <ListRow icon="reflections" title="Co-parenting & Conflict" onClick={() => props.onOpenLibraryTheme('Respond')} />
        <ListRow icon="reflections" title="Court & Legal" onClick={() => props.onOpenLibraryTheme('Court')} />
      </div>
    </RouteFrame>
  );
}

function ProgrammeHome({ props, onBack }: { props: TriageHomeProps; onBack: () => void }) {
  const progress = useProgress();
  const line = programmeProgressLine(props.programme, progress.state);
  const underMarquee = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, margin: '0 0 12px' }}>
      <span style={{ fontFamily: F.ui, fontSize: SIZE.caption, color: C.textMuted }}>{line}</span>
      <button onClick={props.onOpenJourney} style={{ ...quietLink, fontSize: SIZE.caption }}>Your programme map {'›'}</button>
    </div>
  );
  return (
    <>
      <div style={{ padding: '8px 18px 0' }}>
        <BackButton onClick={onBack} />
      </div>
      {props.renderToday({ underMarquee, header: <AreaHeading area="programme" /> })}
    </>
  );
}

function CompassionHome({ props, trialLock, onBack, onProgramme }: { props: TriageHomeProps; trialLock: boolean; onBack: () => void; onProgramme: () => void }) {
  const progress = useProgress();
  const daily = props.programme && progress.state.activeProgramme === 'compassion'
    ? dailyState(props.programme, progress.state, progress.state.devMode)
    : null;
  const meds = ['Loving Kindness', 'Soothing our suffering', 'Free and Loving Self']
    .map((t) => MEDITATIONS.find((m) => m.title === t))
    .filter((m): m is Meditation => !!m);
  const underMarquee = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '0 0 12px' }}>
      <button onClick={props.onOpenJourney} style={{ ...quietLink, fontSize: SIZE.caption }}>Your course map {'›'}</button>
    </div>
  );
  const toProgramme = () => {
    if (trialLock) { props.onLocked?.(); return; }
    progress.setActiveProgramme('main');
    onProgramme();
  };
  const afterFeed = (
    <>
      {daily && daily.locked && !daily.finished && (
        <>
          <SectionLabel>Compassion meditations</SectionLabel>
          <div style={{ display: 'grid', gap: SP.sm }}>
            {meds.map((m) => <ListRow key={m.id} icon="moon" title={m.title} sub={m.minutes ? `${m.minutes} min` : undefined} onClick={() => props.onOpenMeditation(m)} />)}
          </div>
        </>
      )}
      <div style={{ ...(trialLock ? LOCKED_LOOK : null), marginTop: SP.lg, padding: '14px 16px', borderRadius: 16, background: 'linear-gradient(180deg,#ffffff,#fdfbf7)', boxShadow: `${SH}, ${HI}` }}>
        <div style={{ fontFamily: F.title, fontSize: SIZE.h3, fontWeight: W.medium, color: C.text }}>Ready for the deeper work?</div>
        <button onClick={toProgramme} aria-label={trialLock ? 'Continue your Recovery programme, locked, for members' : undefined} style={{ ...quietLink, padding: '6px 0 0' }}>{'Continue your Recovery programme ›'}{trialLock && <span aria-hidden style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 6 }}><Icon name="lock" size={14} sw={2} /></span>}</button>
      </div>
    </>
  );
  return (
    <>
      <div style={{ padding: '8px 18px 0' }}>
        <BackButton onClick={onBack} />
      </div>
      {props.renderToday({ underMarquee, afterFeed, header: <AreaHeading area="compassion" /> })}
    </>
  );
}

function MeditationsView({ props, onBack }: { props: TriageHomeProps; onBack: () => void }) {
  return (
    <RouteFrame area="meditations" onBack={onBack}>
      <SectionLabel>Meditations</SectionLabel>
      <div style={{ display: 'grid', gap: SP.sm }}>
        {MEDITATIONS.map((m) => <ListRow key={m.id} icon="moon" title={m.title} sub={m.minutes ? `${m.minutes} min` : undefined} onClick={() => props.onOpenMeditation(m)} />)}
      </div>
    </RouteFrame>
  );
}
